import { getCurrentDateString } from 'src/utils/date';
import { type LiveTrainingSession } from 'src/pages/MyLearningPage/children/hooks/useLiveTrainingFilters';

interface GenerateLiveTrainingCSVProps {
  userName: string;
  liveTrainingData: LiveTrainingSession[];
}

interface LiveTrainingCSVItem {
  'User Name': string;
  'Content Type': string;
  Title: string;
  'Start Date': string;
  'Completed Date'?: string;
}

const downloadCSVFile = (data: string, userName: string): void => {
  const downloadDate = getCurrentDateString();
  const fileName = `${userName}-${downloadDate}.csv`;

  const blob = new Blob([data], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');

  a.href = url;
  a.download = fileName;

  a.click();
};

const createCSVData = (data: LiveTrainingCSVItem[]): string => {
  const csvRows = [];
  // Define headers explicitly to ensure all columns are always present
  const headers = ['User Name', 'Content Type', 'Title', 'Start Date', 'Completed Date'];

  csvRows.push(headers.join(','));

  data.forEach((el) => {
    const values = headers.map((key) => {
      const value = el[key as keyof LiveTrainingCSVItem];

      // Handle empty values and escape commas in content
      if (value === undefined || value === '') {
        return '';
      }
      // Escape values that contain commas, quotes, or newlines
      if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }

      return value;
    });

    csvRows.push(values.join(','));
  });

  return csvRows.join('\n');
};

const transformDate = (isoDateString?: string): string => {
  if (!isoDateString) {
    return '';
  }

  // Extract YYYY-MM-DD from ISO string
  return isoDateString.split('T')[0];
};

const transformLiveTrainingData = (data: LiveTrainingSession[], userName: string): LiveTrainingCSVItem[] => {
  return data.map((session) => {
    const csvItem: LiveTrainingCSVItem = {
      'User Name': userName,
      'Content Type': 'Live Training',
      Title: session.title,
      'Start Date': transformDate(session.startDateTime),
    };

    // Only include Completed Date if the session is completed
    if (session.status === 'completed') {
      csvItem['Completed Date'] = transformDate(session.endDateTime);
    }

    return csvItem;
  });
};

export const generateLiveTrainingCSV = ({ userName, liveTrainingData }: GenerateLiveTrainingCSVProps): void => {
  const transformedData = transformLiveTrainingData(liveTrainingData, userName);
  const csvData = createCSVData(transformedData);

  downloadCSVFile(csvData, userName);
};
